import React, { useEffect, useState } from "react";
import "./PlanDetailsScreen.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchPlanSummary } from "../../../redux/actions/planSummary";
import { fetchMerchants } from "../../../redux/actions/fetchPlanMerchants";
import { RootState } from "../../../redux/rootReducers";
import { CalendarDays, Copy, User, Search, X } from "lucide-react";
import ReTable from "../../../components/ReTable/ReTable";
import { useTranslation } from "react-i18next";
import { duplicateRecurringPlan } from "../../../redux/actions/duplicateRecurringPlan";
import Layout from "../../Layout/Layout";

const PlanDetailsScreen = ({
  planId,
  token,
}: {
  planId: string;
  token: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [merchantSearch, setMerchantSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(merchantSearch);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const {
    data: plan,
    loading: planLoading,
    error: planError,
  } = useSelector((state: RootState) => state.planSummary);

  const {
    data: merchantPage,
    loading: merchantsLoading,
    error: merchantsError,
  } = useSelector((state: RootState) => state.planMerchants);

  useEffect(() => {
    dispatch(fetchPlanSummary(planId, token));
  }, [dispatch, planId, token]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setMerchantSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleResetSearch = () => {
    setMerchantSearch("");
    setCurrentPage(1);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(merchantSearch);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [merchantSearch]);

  useEffect(() => {
    dispatch(
      fetchMerchants(planId, token, currentPage, pageSize, debouncedSearch)
    );
  }, [dispatch, planId, token, currentPage, pageSize, debouncedSearch]);

  const columns = [
    { label: t("recurring.merchantName"), key: "merchantName" },
    { label: t("recurring.merchantCode"), key: "merchantCode" },
    { label: t("recurring.partnerName"), key: "partnerName" },
  ];

  const duplicatePlan = () => {
    dispatch(duplicateRecurringPlan(planId, token));
  };

  const merchants = merchantPage?.content || [];
  const totalPages = merchantPage?.totalPages || 0;
  const totalElements = merchantPage?.totalElements || 0;

  if (planError || merchantsError)
    return <div>Error: {planError || merchantsError}</div>;
  if (!plan) return null;
  const statCards = [
    {
      title: t("plan.planDetails.totalCardHolders"),
      value: plan.cardHolderSummary?.count ?? 0,
      count: `${plan.cardHolderSummary?.amount} ${plan.cardHolderSummary?.currency}`,
      color: "#FFEB3B",
      icon: "💳",
    },
    {
      title: t("plan.planDetails.totalMerchants"),
      value: plan.totalPartners?.count ?? 0,
      count: `${plan.totalPartners?.amount ?? 0} ${
        plan.totalPartners?.currency ?? "TND"
      }`,
      color: "#FFEB3B",
      icon: "💼",
    },
  ];

  const StatCard = ({ title, value, count, color, icon }: any) => (
    <div
      style={{
        flex: "1",
        minWidth: "200px",
        margin: "10px",
        padding: "16px",
        borderRadius: "16px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        display: "flex",
        alignItems: "center",
        background: "#fff",
      }}
    >
      <div
        style={{
          backgroundColor: color,
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: "20px",
          marginRight: "12px",
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: "13px",
            color: "#888",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {title} <span style={{ color }}>{count}</span>
        </div>
        <div style={{ fontWeight: "bold", fontSize: "16px" }}>{value}</div>
      </div>
    </div>
  );
  return (
    <Layout type="admin">
      <div className="content-side">
        <div className="plan-details-container">
          <div className="breadcrumb">
            Softpos / Recurring Plans / {plan.planName}
          </div>

          <div className="topbar">
            <h1>{plan.planName}</h1>
            <div className="search-section">
              <input
                className="search-box"
                placeholder={t("plan.planDetails.searchMerchant")}
                value={merchantSearch}
                onChange={handleSearchChange}
              />
              {merchantSearch && (
                <button
                  type="button"
                  className="absolute top-2 right-2"
                  onClick={handleResetSearch}
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          <div className="plan-info-box">
            <div className="left-info">
              {plan.numberOfPayments ? (
                <div className="text-content">
                  {t("plan.planDetails.fixedPayments", {
                    numberOfPayments: plan.numberOfPayments,
                    maxAmount: plan.maxAmount?.amount,
                    maxCurrency: plan.maxAmount?.currency,
                    minAmount: plan.minAmount?.amount,
                    minCurrency: plan.minAmount?.currency,
                    maxPayableAmount: plan.maxPayableAmountPerFrequency?.amount,
                    maxPayableCurrency:
                      plan.maxPayableAmountPerFrequency?.currency,
                  })}
                </div>
              ) : (
                <div className="text-content">
                  {t("plan.planDetails.termBased", {
                    term: plan.term,
                    frequency: plan.paymentFrequency?.toLowerCase(),
                    maxAmount: plan.maxAmount?.amount,
                    maxCurrency: plan.maxAmount?.currency,
                    minAmount: plan.minAmount?.amount,
                    minCurrency: plan.minAmount?.currency,
                    maxPayableAmount: plan.maxPayableAmountPerFrequency?.amount,
                    maxPayableCurrency:
                      plan.maxPayableAmountPerFrequency?.currency,
                  })}
                </div>
              )}

              <div
                style={{ display: "flex", flexWrap: "wrap", marginTop: "16px" }}
              >
                {statCards.map((card, index) => (
                  <StatCard key={index} {...card} />
                ))}
              </div>
            </div>

            <div className="right-info">
              <div className="meta-row">
                <CalendarDays size={16} />
                <span>
                  {t("plan.planDetails.creationDate")}{" "}
                  {new Date(plan.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div className="meta-row">
                <User size={16} />
                <span>
                  {t("plan.planDetails.createdBy")}{" "}
                  {plan.createdBy || "[Creator name]"}
                </span>
              </div>
              <div className="meta-row">
                <span>{t("plan.planDetails.planStatus")}</span>{" "}
                <span className={`status-${plan.status?.toLowerCase()}`}>
                  {plan.status}
                </span>
              </div>
              <button className="duplicate-button" onClick={duplicatePlan}>
                <Copy size={16} /> {t("plan.planDetails.duplicate")}
              </button>
            </div>
          </div>

          <div className="merchants-section">
            <h2>{t("plan.planDetails.merchants")}</h2>
            <div className="tw-px-4" style={{ padding: 4 }}>
              <ReTable
                data={merchants}
                columns={columns}
                pageSize={pageSize}
                currentPageProp={currentPage}
                page={currentPage}
                resultPageLength={merchants.length}
                totalMatches={totalElements}
                totalPages={totalPages}
                onPageChange={(newPage) => setCurrentPage(newPage)}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setCurrentPage(1);
                }}
                loading={merchantsLoading}
                isTableDetailRow={true}
                isPagination={true}
                showFilterButton={false}
              />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PlanDetailsScreen;
